import type { LibrarySketch } from './types';

export const typographySketches: LibrarySketch[] = [
  {
    id: 'typography-kinetic-text',
    name: 'Kinetic Text Explosion',
    description: 'Text characters explode apart on click and smoothly reform, creating a kinetic typography effect.',
    category: 'typography',
    tags: ['text', 'kinetic', 'explosion', 'typography', 'animation'],
    runtimeMode: 'p5',
    inputTypes: ['text'],
    params: [
      { name: 'fontSize', type: 'int', min: 24, max: 120, default: 64, label: 'Font Size' },
      { name: 'explodeForce', type: 'float', min: 2, max: 20, default: 8, label: 'Explode Force' },
      { name: 'returnSpeed', type: 'float', min: 0.02, max: 0.2, default: 0.05, label: 'Return Speed' },
      { name: 'textColor', type: 'color', default: '#ffffff', label: 'Color' },
    ],
    code: `// @param int fontSize 24 120 64
// @param float explodeForce 2 20 8
// @param float returnSpeed 0.02 0.2 0.05
// @param color textColor #ffffff

let chars = [], exploded = false;
function setup() {
  createCanvas(genie.width, genie.height);
  textAlign(CENTER, CENTER);
  rebuildText();
  // The connected text can change while the sketch runs.
  genie.onInput('text', rebuildText);
}
function rebuildText() {
  chars = [];
  const txt = genie.inputText || 'GENIE';
  const fs = genie.params.fontSize ?? 64;
  textSize(fs);
  const totalW = textWidth(txt);
  const startX = (width - totalW) / 2;
  let cx = startX;
  for (let i = 0; i < txt.length; i++) {
    const cw = textWidth(txt[i]);
    chars.push({
      char: txt[i], homeX: cx + cw / 2, homeY: height / 2,
      x: cx + cw / 2, y: height / 2, vx: 0, vy: 0
    });
    cx += cw;
  }
}
function mousePressed() {
  const ef = genie.params.explodeForce ?? 8;
  for (const c of chars) {
    c.vx = random(-ef, ef);
    c.vy = random(-ef, ef);
  }
  exploded = true;
}
function draw() {
  background(0);
  const rs = genie.params.returnSpeed ?? 0.05;
  const fs = genie.params.fontSize ?? 64;
  const col = color(genie.params.textColor ?? '#ffffff');
  textSize(fs);
  fill(col);
  noStroke();
  for (const c of chars) {
    if (exploded) {
      c.vx += (c.homeX - c.x) * rs;
      c.vy += (c.homeY - c.y) * rs;
      c.vx *= 0.92; c.vy *= 0.92;
      c.x += c.vx; c.y += c.vy;
    }
    const d = dist(c.x, c.y, c.homeX, c.homeY);
    const a = map(d, 0, 200, 255, 100);
    const r = map(d, 0, 100, 0, 0.3);
    push();
    translate(c.x, c.y);
    rotate(r * (c.vx > 0 ? 1 : -1));
    fill(red(col), green(col), blue(col), a);
    text(c.char, 0, 0);
    pop();
  }
}`,
  },

  {
    id: 'typography-matrix-rain',
    name: 'Matrix Rain',
    description: 'Classic Matrix-style falling character rain with randomized speeds, brightness, and character cycling.',
    category: 'typography',
    tags: ['matrix', 'rain', 'code', 'hacker', 'falling'],
    runtimeMode: 'p5',
    inputTypes: [],
    params: [
      { name: 'fontSize', type: 'int', min: 8, max: 24, default: 14, label: 'Font Size' },
      { name: 'speed', type: 'float', min: 0.5, max: 5.0, default: 1.5, label: 'Speed' },
      { name: 'density', type: 'float', min: 0.3, max: 1.0, default: 0.7, label: 'Density' },
      { name: 'fadeSpeed', type: 'int', min: 10, max: 80, default: 30, label: 'Fade Speed' },
    ],
    code: `// @param int fontSize 8 24 14
// @param float speed 0.5 5.0 1.5
// @param float density 0.3 1.0 0.7
// @param int fadeSpeed 10 80 30

const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@#$%^&*';
let streams = [];
function setup() {
  createCanvas(genie.width, genie.height);
  const fs = genie.params.fontSize ?? 14;
  const cols = floor(width / fs);
  const dens = genie.params.density ?? 0.7;
  for (let i = 0; i < cols; i++) {
    if (random() < dens) {
      streams.push({
        x: i * fs, y: random(-height, 0),
        speed: random(2, 8), chars: [],
        len: floor(random(5, 30))
      });
      for (let j = 0; j < streams[streams.length - 1].len; j++) {
        streams[streams.length - 1].chars.push(chars[floor(random(chars.length))]);
      }
    }
  }
}
function draw() {
  const fs = genie.params.fontSize ?? 14;
  const spd = genie.params.speed ?? 1.5;
  const fade = genie.params.fadeSpeed ?? 30;
  background(0, fade);
  textFont('monospace');
  textSize(fs);
  textAlign(CENTER);
  noStroke();
  for (const s of streams) {
    for (let j = 0; j < s.len; j++) {
      const cy = s.y - j * fs;
      if (cy < -fs || cy > height + fs) continue;
      if (j === 0) {
        fill(180, 255, 180);
      } else {
        const brightness = map(j, 0, s.len, 200, 40);
        fill(0, brightness, 0);
      }
      if (random() < 0.02) {
        s.chars[j] = chars[floor(random(chars.length))];
      }
      text(s.chars[j], s.x + fs / 2, cy);
    }
    s.y += s.speed * spd;
    if (s.y - s.len * fs > height) {
      s.y = random(-200, -50);
      s.speed = random(2, 8);
    }
  }
}`,
  },
];
